/**
 * Formatiert einen Geldbetrag in Euro mit deutscher Lokalisierung
 * @returns Formatierter String (z.B. "2.850.000 €")
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

/**
 * Formatiert einen Geldbetrag in Millionen Euro
 * @returns Formatierter String (z.B. "2,85 Mio. €")
 */
export const formatCurrencyMillions = (value: number): string => {
  const millions = value / 1000000;
  return `${formatNumber(millions)} Mio. €`;
};

/**
 * Formatiert einen Prozentwert mit einer Nachkommastelle
 * @returns Formatierter String (z.B. "8,5")
 */
export const formatPercentage = (value: number | null): string => {
  if (value === null) return "-";
  return formatNumber(value, 1);
};

/**
 * Formatiert einen Prozentwert mit zwei Nachkommastellen
 * @returns Formatierter String (z.B. "8,52")
 */
export const formatPercentagePrecise = (value: number): string => {
  return formatNumber(value, 2);
};

/**
 * Formatiert ein Datum im deutschen Format
 * @returns Formatierter String (z.B. "15.03.2023")
 */
export const formatDate = (date: Date | string): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return dateObj.toLocaleDateString("de-DE");
};

/**
 * Formatiert ein Datum im langen deutschen Format
 * @returns Formatierter String (z.B. "15. März 2023")
 */
export const formatDateLong = (date: Date | string): string => {
  const dateObj = typeof date === "string" ? new Date(date) : date;
  return dateObj.toLocaleDateString("de-DE", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

/**
 * Formatiert einen Energiewert in kWh ohne Nachkommastellen mit deutscher Lokalisierung
 * @returns Formatierter String ohne Nachkommastellen (z.B. "1.234.567 kWh")
 */
export const formatEnergyKWh = (value: number): string => {
  return formatNumberWithoutDecimals(value) + " kWh";
};

/**
 * Formatiert eine Dezimalzahl ohne Nachkommastellen mit deutscher Lokalisierung
 * @returns Formatierter String ohne Nachkommastellen (z.B. "1.234.567")
 */
export const formatNumberWithoutDecimals = (value: number): string => {
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(value));
};

/**
 * Formatiert eine Anzahl mit Tausendertrennzeichen und optionalen Nachkommastellen
 * @returns Formatierter String (z.B. "1.234" oder "1.234,56")
 */
export const formatNumber = (value: number, decimals: number = 0): string => {
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
};

/**
 * Formatiert eine Leistung in MW mit deutscher Formatierung
 * @returns Formatierter String (z.B. "6,8")
 */
export const formatPowerMW = (value: number, decimals: number = 1): string => {
  return formatNumber(value, decimals);
};

/**
 * Formatiert eine Leistung in kW mit deutscher Formatierung
 * @returns Formatierter String (z.B. "6.800")
 */
export const formatPowerKW = (value: number, decimals: number = 0): string => {
  return formatNumber(value, decimals);
};

/**
 * Formatiert eine Energieproduktion in MWh mit deutscher Formatierung
 * @returns Formatierter String (z.B. "22.179,2")
 */
export const formatEnergyMWh = (
  value: number,
  decimals: number = 1,
): string => {
  return formatNumber(value, decimals);
};

/**
 * Formatiert eine Länge in Metern mit deutscher Formatierung
 * @returns Formatierter String (z.B. "163,0")
 */
export const formatLengthM = (value: number, decimals: number = 1): string => {
  return formatNumber(value, decimals);
};

/**
 * Formatiert eine Geschwindigkeit in m/s mit deutscher Formatierung
 * @returns Formatierter String (z.B. "7,42")
 */
export const formatSpeedMS = (value: number, decimals: number = 2): string => {
  return formatNumber(value, decimals);
};

/**
 * Formatiert Koordinaten mit deutscher Formatierung
 * @returns Formatierter String (z.B. "6,819141° Ost, 49,587791° Nord")
 */
export const formatCoordinates = (
  longitude: number,
  latitude: number,
  decimals: number = 6,
): string => {
  const lon = formatNumber(longitude, decimals);
  const lat = formatNumber(latitude, decimals);
  return `${lon}° Ost, ${lat}° Nord`;
};

/**
 * Bestimmt das Icon für einen Projektstatus
 * @returns Icon-Name für Material-UI
 */
export const getStatusIcon = (status: string): string => {
  switch (status) {
    case "Laufend":
      return "play_circle";
    case "Abgeschlossen":
      return "check_circle";
    case "Entwurf":
      return "edit";
    default:
      return "help";
  }
};

/**
 * Formatiert einen Status-Text für die Anzeige
 * @returns Formatierter Status-Text
 */
export const formatStatus = (status: string): string => {
  const statusMap: { [key: string]: string } = {
    entwurf: "Entwurf",
    laufend: "Laufend",
    abgeschlossen: "Abgeschlossen",
    pausiert: "Pausiert",
    abgebrochen: "Abgebrochen",
  };

  return statusMap[status.toLowerCase()] || status;
};

/**
 * Formatiert einen Hersteller-Namen für die Anzeige
 * @returns Formatierter Hersteller-Name
 */
export const formatManufacturer = (manufacturer: string): string => {
  const manufacturerMap: { [key: string]: string } = {
    nordex: "NORDEX",
    vestas: "Vestas",
    "siemens gamesa": "Siemens Gamesa",
    ge: "GE",
    enercon: "Enercon",
  };

  const lowerManufacturer = manufacturer.toLowerCase();
  return manufacturerMap[lowerManufacturer] || manufacturer;
};

/**
 * Formatiert einen Modell-Namen für die Anzeige
 * @returns Formatierter Modell-Name
 */
export const formatModel = (model: string): string => {
  // Entferne lange Beschreibungen in Klammern für bessere Lesbarkeit
  return model.replace(/\s*\([^)]*\)/g, "");
};

/**
 * Validiert ob ein String nicht leer ist
 * @returns true wenn nicht leer
 */
export const isNotEmpty = (value: string): boolean => {
  return value.trim().length > 0;
};

/**
 * Validiert ob ein numerischer Wert positiv ist
 * @returns true wenn positiv
 */
export const isPositive = (value: number): boolean => {
  return value > 0;
};

/**
 * Validiert ob ein Prozentwert im gültigen Bereich (0-100) liegt
 * @returns true wenn im gültigen Bereich
 */
export const isValidPercentage = (value: number): boolean => {
  return value >= 0 && value <= 100;
};

/**
 * Konvertiert ein Datum sicher für HTML date input Felder
 * @returns Formatierter String im YYYY-MM-DD Format oder leerer String
 */
export const formatDateForInput = (date: Date | string): string => {
  try {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
      return "";
    }
    return dateObj.toISOString().split("T")[0];
  } catch (error) {}

  return "";
};

/**
 * Formatiert einen Cent-Betrag mit zwei Nachkommastellen
 * @returns Formatierter String mit zwei Nachkommastellen (z.B. "5,25")
 */
export const formatCentAmount = (value: number): string => {
  return new Intl.NumberFormat("de-DE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

/**
 * Formatiert die foreign_id durch Entfernen der Unterstriche am Ende
 * @returns Formatierte ID ohne trailing underscores
 */
export const formatForeignId = (foreignId?: string): string => {
  if (!foreignId) return "";
  return foreignId.replace(/_+$/, "");
};
